import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';
import { getReportingCurrencyISO } from '@/utilities/getReportingCurrencyISO';

import { validateIsBetween, INPUT_VALUE_RANGES } from './validation';
import { isNumber } from 'lodash';
import { getJsonField } from '@/utilities/object';
import type { NarrativeValuatorCompany } from '../hooks/narrativeValuatorCompanyFragment';
import { DEFAULT_DISCOUNT_RATE_PERCENTAGE } from '../components/Calculators';

export const getCompanyFinancialData = (company: NarrativeValuatorCompany) => {
  const {
    analysisFuture,
    analysisPast,
    analysisValue,
    data,
    primaryIndustry,
    secondaryIndustry,
  } = company;
  const { intrinsicValue, lastSharePrice, pe } = analysisValue ?? {};
  const costOfEquity = getJsonField<number>(
    intrinsicValue,
    'cost_of_equity',
    isNumber
  );
  const { netIncomeMargin } = analysisPast ?? {};
  const { marketCap } = data;

  const reportedMarketCapitalisation =
    getJsonField<number>(marketCap, 'reported', isNumber) || 0;
  const listingMarketCapitalisation =
    getJsonField<number>(marketCap, 'listing', isNumber) || 0;

  const reportingCurrencyISO =
    getReportingCurrencyISO(data.currencyInfo) || 'XXX';
  const listingCurrencyISO =
    getTradingItemCurrencyISO(data.currencyInfo) || 'XXX';

  const { revenueGrowthAnnual, forwardPe1Y } = analysisFuture ?? {};

  const discountRatePercentage = costOfEquity !== null ? costOfEquity * 100 : 0;
  const priceEarningsMultiple = pe !== null ? pe : 0;
  const profitMarginPercentage =
    typeof netIncomeMargin === 'number' ? netIncomeMargin * 100 : 0;
  const revenuePercentage =
    typeof revenueGrowthAnnual === 'number' ? revenueGrowthAnnual * 100 : 0;
  const forwardPriceEarningsMultiple1Y = forwardPe1Y !== null ? forwardPe1Y : 0;
  const secondaryIndustryFriendlyName =
    secondaryIndustry?.friendlyName ?? secondaryIndustry?.name;
  const secondaryIndustryAveragesMap = Object.fromEntries(
    secondaryIndustry?.industryAverages?.map(({ name, value }) => [
      name,
      value,
    ]) ?? []
  );

  const primaryIndustryFriendlyName =
    primaryIndustry?.friendlyName ?? primaryIndustry?.name;
  const primaryIndustryAveragesMap = Object.fromEntries(
    primaryIndustry?.industryAverages?.map(({ name, value }) => [
      name,
      value,
    ]) ?? []
  );

  const industryRevenueGrowth =
    secondaryIndustryAveragesMap['future_revenue_growth_3y'];
  const industryPriceEarningMultiple = secondaryIndustryAveragesMap['pe'];
  const secondaryIndustryNetProfitMargin =
    secondaryIndustryAveragesMap['net_income_margin_1y'];
  const primaryIndustryNetProfitMargin =
    primaryIndustryAveragesMap['net_income_margin_1y'];

  const secondaryIndustryNetProfitMarginPercentage =
    secondaryIndustryNetProfitMargin
      ? secondaryIndustryNetProfitMargin * 100
      : 0;
  const primaryIndustryNetProfitMarginPercentage =
    primaryIndustryNetProfitMargin ? primaryIndustryNetProfitMargin * 100 : 0;

  const isInvalidDiscountRatePercentage = !!validateIsBetween(
    discountRatePercentage,
    INPUT_VALUE_RANGES.discountRatePercentage.min,
    INPUT_VALUE_RANGES.discountRatePercentage.max,
    'Discount rate'
  );

  return {
    discountRatePercentage: isInvalidDiscountRatePercentage
      ? DEFAULT_DISCOUNT_RATE_PERCENTAGE
      : discountRatePercentage,
    forwardPriceEarningsMultiple1Y,
    industryPriceEarningMultiple,
    industryRevenueGrowth,
    industryNetProfitMarginPercentage:
      secondaryIndustryNetProfitMarginPercentage ||
      primaryIndustryNetProfitMarginPercentage ||
      0,
    priceEarningsMultiple,
    secondaryIndustryFriendlyName,
    primaryIndustryFriendlyName,
    profitMarginPercentage,
    revenuePercentage,
    lastSharePrice,
    listingCurrencyISO,
    listingMarketCapitalisation,
    reportedMarketCapitalisation,
    reportingCurrencyISO,
  };
};
